// Public types for the browser-side gateway + verifier service worker (szzt-spec.md §9, §12).
//
// The serving core (index, routing, headers, verifying cache, serve) is PURE and transport-agnostic:
// it operates on plain data and re-derives every CID from signed state via @szzt/records, never
// touching a network library directly. The one seam it needs is `Fetcher` (byte retrieval); the
// service-worker shell (sw.ts) and any server gateway supply a concrete implementation.

import type { Routing } from "@szzt/core";

/** Which serving context to render in: networked installable PWA vs sandboxed embeddable tile (§9.4, §9.6). */
export type ServeMode = "site" | "tile";

/** A resource located in the signed manifest (§9.2 index): path → verified blob + serving metadata. */
export interface ResourceLocation {
  path: string;
  /** The resource's `raw` blob CID (manifest `src.ref.$link`). */
  cid: string;
  /** Exact byte size asserted by the manifest (`src.size`). */
  size: number;
  /** True content type served for the path (§3.4). */
  contentType: string;
  /** Publisher-supplied CSP, applied only in site mode and overridden in tile mode (§3.4, §9.6). */
  csp?: string;
  sourcemap?: string;
}

/**
 * Where a bare content CID resolves, for `/.well-known/rasl/<cid>` (§9.2). A resource CID → its blob;
 * the root CID → the canonical dCBOR42 re-encoding of the inline manifest (NOT a blob).
 */
export type CidLocation =
  | { kind: "blob"; did: string; cid: string; size: number; contentType: string; path: string }
  | { kind: "root"; cid: string; bytes: Uint8Array };

/** Everything needed to serve a site, built ONLY from signed state (§9.2). */
export interface SzztIndex {
  /** DID of the repo that owns the manifest record and its resource blobs. */
  did: string;
  /** dCBOR42 root CID of the inline MASL (§3.3), re-derived and matched against the signed pins. */
  rootCid: string;
  name: string;
  /** Entry document path from the deployment record, when one was resolved (§4.2). */
  entry?: string;
  routing?: Routing;
  /** path → resource location. */
  resources: Map<string, ResourceLocation>;
  /** bare content CID → location (§9.2 CID→location index). */
  cidToLocation: Map<string, CidLocation>;
  /**
   * The site record's `hints` mirror hosts (§4.1, §13), present ONLY when the index was resolved via a
   * site AT-URI (a deployment / root-CID / identity target has no site record, so no hints). A deployment
   * consumes these to build a `MirrorFetcher` (`src/mirror.ts`); the pure serving core ignores them.
   */
  hints?: readonly string[];
}

/** A request reduced to what routing + serving need. */
export interface ServeRequest {
  /** Absolute, `/`-rooted request path (a URL pathname; no query or fragment). */
  path: string;
  /** True when this is a navigation (Sec-Fetch-Mode: navigate, or Accept includes text/html) — gates SPA fallback (§9.9). */
  navigation: boolean;
}

/** The result of routing a request path against the manifest (§9.9); first match wins. */
export type RouteResult =
  | { kind: "redirect"; status: number; location: string }
  | { kind: "resource"; status: number; path: string; location: ResourceLocation }
  | { kind: "notFound"; status: 404; location?: ResourceLocation };

/** A fully-resolved serving decision: status + headers + body. */
export interface ServeDecision {
  status: number;
  headers: Record<string, string>;
  /** Verified content bytes, a text diagnostic (fail-closed / default 404), or null (redirect / bodyless). */
  body: Uint8Array | string | null;
}

/** Minimal byte-fetch seam the verifying cache uses. A @szzt/records `RepoReader` satisfies it. */
export interface Fetcher {
  /**
   * Fetch a blob's exact bytes by owning DID + `raw` CID. The implementation is responsible for the
   * SSRF constraints of §9.1 (public-HTTPS-only, no private ranges, pinned endpoints) — see ssrf.ts.
   */
  getBlob(did: string, cid: string): Promise<Uint8Array>;
}

/** Thrown when the gateway cannot build a serving index or a requested CID is not in signed state. */
export class GatewayError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GatewayError";
  }
}

/** Thrown when fetched bytes do not hash to the CID the signed manifest pins (§9.4 fail-closed). */
export class VerificationError extends Error {
  readonly path: string;
  readonly expectedCid: string;
  readonly actualCid: string;
  constructor(path: string, expectedCid: string, actualCid: string) {
    super(`verification failed for "${path}": manifest pins ${expectedCid} but bytes hash to ${actualCid}`);
    this.name = "VerificationError";
    this.path = path;
    this.expectedCid = expectedCid;
    this.actualCid = actualCid;
  }
}

/** Thrown when unknown-CID lookups exceed the read-path rate limit (§12, threat A.4 #12 amplification). */
export class RateLimitError extends Error {
  readonly cid: string;
  constructor(cid: string) {
    super(`unknown-CID lookup rate limit exceeded for ${cid}`);
    this.name = "RateLimitError";
    this.cid = cid;
  }
}

/**
 * Thrown when a value copied verbatim from signed state (a MASL `content-type`/`content-security-policy`,
 * or a routing redirect target) contains a CR/LF or other control character. Node's `res.writeHead()`
 * throws `ERR_INVALID_CHAR` on such a value; left unguarded, that throw reaches an unawaited handler and
 * takes down the whole process (roadmap H2) — one hostile site outing every tenant. Caught by `serve.ts`
 * (and `@szzt/server`'s `dispatch.ts`) to fail closed with a diagnostic response for that ONE request.
 */
export class UnsafeHeaderError extends Error {
  readonly field: string;
  constructor(field: string, value: string) {
    super(`unsafe header value for "${field}": contains a control character (${JSON.stringify(value)})`);
    this.name = "UnsafeHeaderError";
    this.field = field;
  }
}
